import { faArrowDown, faArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, SxProps, Text } from '@modusign/design-system';
import OutsideClickHandler from 'react-outside-click-handler';
import { useToggle } from 'react-use';

import OrderIndicator from './OrderIndicator';

interface SortableListHeaderProps extends SxProps {
  disabled?: boolean;
  name: string;
  order: 'asc' | 'desc' | null;
  ascDescription?: string;
  descDescription?: string;
  dropdownHorizontalOffset?: number;
  dropdownVerticalOffset?: number;
  onOrderClick: (order: 'asc' | 'desc') => void;
}

export default function SortableListHeader({
  disabled,
  name,
  order,
  ascDescription,
  descDescription,
  dropdownHorizontalOffset,
  dropdownVerticalOffset = 13,
  onOrderClick,
  sx,
}: SortableListHeaderProps) {
  const [isDropdownOpen, toggleDropdown] = useToggle(false);

  return (
    <Box position="relative">
      <OutsideClickHandler disabled={!isDropdownOpen} onOutsideClick={() => toggleDropdown(false)}>
        <Flex
          alignItems="center"
          as="button"
          sx={sx}
          type="button"
          onClick={disabled ? undefined : toggleDropdown}
        >
          <Text color="textNeutralVeryLight">{name}</Text>
          <OrderIndicator order={order} sx={{ ml: 1 }} />
        </Flex>
        {isDropdownOpen && (
          <Box
            as="ol"
            bg="menuBorderNeutralLightest"
            border="1px solid"
            borderColor="menuBorderNeutralBase"
            borderRadius="4px"
            boxShadow="0px 2px 6px rgba(0, 0, 0, 0.101961)"
            left={dropdownHorizontalOffset || 0}
            position="absolute"
            py={3}
            top={`calc(100% + ${dropdownVerticalOffset}px)`}
            width="200px"
            zIndex={999}
          >
            <DropdownItem
              description={ascDescription}
              order="asc"
              onClick={() => {
                onOrderClick('asc');
                toggleDropdown();
              }}
            />
            <DropdownItem
              description={descDescription}
              order="desc"
              onClick={() => {
                onOrderClick('desc');
                toggleDropdown();
              }}
            />
          </Box>
        )}
      </OutsideClickHandler>
    </Box>
  );
}

interface DropdownItemProps {
  order: 'asc' | 'desc';
  description?: string;
  onClick: () => void;
}

function DropdownItem({ order, description, onClick }: DropdownItemProps) {
  return (
    <Box as="li" width="100%">
      <Flex
        alignItems="center"
        as="button"
        px={4}
        py={2}
        sx={{
          '&:hover': {
            bg: 'lightGray50',
          },
        }}
        type="button"
        width="100%"
        onClick={onClick}
      >
        <Text
          color="iconNeutralSlightlyDark"
          fontSize="12px"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '16px',
            height: '16px',
          }}
        >
          <FontAwesomeIcon
            icon={order === 'asc' ? faArrowUp : faArrowDown}
            style={{ flex: 'none' }}
          />
        </Text>
        <Text color="textNeutralBase" ml={1}>
          {order === 'asc' ? '오름차순' : '내림차순'} 정렬{description && ` (${description})`}
        </Text>
      </Flex>
    </Box>
  );
}
